import { TextField } from '@warp-ds/react';
import { type Dispatch, type ReactNode, useMemo, useState } from 'react';

import { t } from 'shared/locale/texts';
import { useConfig } from '../../config/ConfigContext';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { usePriceLevels } from '../../hooks/usePriceLevels';
import actions from '../../search/flight/flightActions';
import type { InvalidFieldMap, ValidationErrorMap } from '../../search/flight/hooks/useFlightValidation';
import type { FlightSearchForm, TripType } from '../../types/search';
import { getISOFormattedDate, getOneYearFromToday, type PriceLevelType } from '../../util/calendar';
import { addMonths, endOfMonth, setDate, startOfMonth, subMonths } from '../../util/datetime-fns';
import { BasicDatePicker } from '../basicDatePicker/BasicDatePicker';
import { ExplanationBox } from './ExplanationBox/ExplanationBox';
import styles from './MetaDatePickerWrapper.module.css';
import { PriceLevelDisplay } from './PriceLevelDisplay/PriceLevelDisplay';

type FlightAction = ReturnType<(typeof actions)[keyof typeof actions]>;

interface MetaDatePickerWrapperProps {
    startDate: Date | null;
    endDate?: Date | null;
    form: FlightSearchForm;
    invalid?: InvalidFieldMap;
    validationErrors: ValidationErrorMap;
    dispatch: Dispatch<FlightAction>;
    isReturnPicker?: boolean;
    openJawNumber?: number | null;
}

export const MetaDatePickerWrapper = ({
    startDate,
    endDate,
    form,
    invalid,
    validationErrors,
    dispatch,
    isReturnPicker,
    openJawNumber = null,
}: MetaDatePickerWrapperProps) => {
    const [displayedDate, setDisplayedDate] = useState(new Date());

    const { flightApiUrl } = useConfig();
    const { origin, destination, departureDate, returnDate, tripType } = form;

    const isSmallScreen = useBreakpoints({ breakpoint: 640 });
    const isWarpSM = useBreakpoints({ breakpoint: 'sm' });

    const isOpenJaw = tripType === 'openjaw';
    const useRange = tripType === 'roundtrip';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const handleDateChange = (dates: { startDate: Date | null; endDate?: Date | null }) => {
        if (tripType === 'roundtrip') {
            dispatch(actions.setDates({ startDate: dates.startDate, endDate: dates.endDate ?? null }));
        } else if (tripType === 'oneway') {
            dispatch(actions.setDates({ startDate: dates.startDate }));
        } else if (tripType === 'openjaw') {
            dispatch(actions.setDates({ startDate: dates.startDate, endDate: returnDate }));
        }
        if (tripType === 'openjaw' && isReturnPicker) {
            dispatch(
                actions.setDates({
                    startDate: departureDate,
                    endDate: dates.startDate,
                }),
            );
        }
    };

    const dynamicStartDate = useMemo(() => {
        if (isSmallScreen) {
            return startOfMonth(subMonths(1, displayedDate));
        }
        return setDate(15, subMonths(1, startOfMonth(displayedDate)));
    }, [displayedDate, isSmallScreen]);

    const dynamicEndDate = useMemo(() => {
        if (isSmallScreen) {
            return endOfMonth(addMonths(1, displayedDate));
        }
        return setDate(15, addMonths(2, displayedDate));
    }, [displayedDate, isSmallScreen]);

    const { data } = usePriceLevels({
        url: `${flightApiUrl}/lowfare/calendar`,
        tripType: tripType as TripType,
        origin: origin ? origin : undefined,
        destination: destination ? destination : undefined,
        startDate: dynamicStartDate,
        endDate: dynamicEndDate,
    });

    const { dateLevels, dateData } = useMemo(() => {
        if (!Array.isArray(data)) {
            return { dateLevels: {}, dateData: {} };
        }

        return data.reduce(
            (acc, { date, level }) => {
                acc.dateLevels[date] = level;
                acc.dateData[date] = <PriceLevelDisplay priceLevel={level as PriceLevelType} />;
                return acc;
            },
            {
                dateLevels: {} as Record<string, string>,
                dateData: {} as Record<string, ReactNode>,
            },
        );
    }, [data]);

    const formatDynamicClassName = (date: Date) => {
        const isoDate = getISOFormattedDate(date);
        const level = dateLevels?.[isoDate] || '';
        return level.toLowerCase();
    };

    const joinErrors = (errors: Array<string | null | undefined>): string => {
        const parts = Array.from(new Set(errors.filter(Boolean) as string[]));
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0];

        const departureMsg = t('flight.validation.departureDate');
        const returnMsg = t('flight.validation.returnDate');

        if (parts.includes(departureMsg) && parts.includes(returnMsg)) {
            return t('flight.validation.bothDates');
        }

        if (parts.length === 2) return `${parts[0]} og ${parts[1]}`;
        return parts.join('. ');
    };

    const anchorErrorText = joinErrors([
        invalid?.departureDate ? validationErrors.departureDate : undefined,
        invalid?.returnDate ? validationErrors.returnDate : undefined,
        invalid?.returnDateAfterDeparture ? validationErrors.returnDateAfterDeparture : undefined,
    ]);

    if (useRange) {
        return (
            <BasicDatePicker
                className={styles.MetaDatePicker}
                modalFooter={<ExplanationBox />}
                dateData={dateData}
                dayClassName={formatDynamicClassName}
                onDateChange={handleDateChange}
                visibleMonths={2}
                useRange={true}
                startDate={startDate}
                endDate={endDate}
                setDisplayedDate={setDisplayedDate}
                upperDateLimit={getOneYearFromToday()}
                useAnchor={
                    isWarpSM
                        ? {
                              title: 'Når vil du reise?',
                              invalid: invalid?.departureDate || invalid?.returnDate,
                              helpText: anchorErrorText || undefined,
                          }
                        : undefined
                }>
                <TextField
                    id={`date-${tripType}-outbound`}
                    data-testid={`date-${tripType}-outbound`}
                    label={t('flight.outbound.label')}
                    invalid={invalid?.departureDate}
                    helpText={validationErrors.departureDate}
                />
                <TextField
                    id={`date-${tripType}-inbound`}
                    data-testid={`date-${tripType}-inbound`}
                    label={t('flight.inbound.label')}
                    invalid={invalid?.returnDate}
                    helpText={validationErrors.returnDate}
                />
            </BasicDatePicker>
        );
    }

    return (
        <BasicDatePicker
            className={styles.MetaDatePicker}
            modalFooter={!isOpenJaw && <ExplanationBox />}
            dateData={dateData}
            dayClassName={formatDynamicClassName}
            onDateChange={handleDateChange}
            visibleMonths={2}
            useRange={false}
            startDate={startDate}
            setDisplayedDate={setDisplayedDate}
            upperDateLimit={getOneYearFromToday()}
            disabledDateRanges={isReturnPicker && form.departureDate ? [{ start: today, end: form.departureDate }] : []}
            useAnchor={
                isWarpSM
                    ? {
                          title: 'Når vil du reise?',
                          invalid: invalid?.departureDate || invalid?.returnDate,
                          helpText: anchorErrorText || undefined,
                      }
                    : undefined
            }>
            <TextField
                id={`date-${tripType}-outbound${openJawNumber ? `-${openJawNumber}` : ''}`}
                data-testid={`date-${tripType}-outbound${openJawNumber ? `-${openJawNumber}` : ''}`}
                label={t('flight.outbound.label')}
                invalid={invalid?.departureDate}
                helpText={validationErrors.departureDate}
            />
        </BasicDatePicker>
    );
};
